import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Sun, Moon, ArrowUpRight } from "lucide-react";
import { navLinks, personal } from "../data/mock";
import { useTheme } from "../context/ThemeContext";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggle } = useTheme();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-40 w-full border-b hairline backdrop-blur-md transition-colors ${
        scrolled ? "bg-background/85" : "bg-background/60"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-cursor-hover>
          <div className="w-8 h-8 rounded-md border hairline grid place-items-center font-mono text-[11px] font-semibold tracking-tight">
            HT
          </div>
          <div className="hidden sm:block">
            <div className="text-sm font-medium leading-none">{personal.name}</div>
            <div className="text-[11px] font-mono text-muted-foreground mt-1 leading-none">
              {personal.role}
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((n) => (
            <NavLink
              key={n.path}
              to={n.path}
              end={n.path === "/"}
              className={({ isActive }) =>
                `link-grow text-sm font-medium ${
                  isActive
                    ? "text-foreground is-active"
                    : "text-muted-foreground hover:text-foreground"
                }`
              }
              data-cursor-hover
            >
              <span className="font-mono text-[10px] text-muted-foreground mr-1">
                {n.index}
              </span>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            aria-label="Toggle theme"
            className="w-10 h-10 rounded-md border hairline grid place-items-center hover:bg-muted transition-colors"
            data-cursor-hover
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <Link
            to="/contact"
            className="hidden md:inline-flex btn-primary !py-2 !px-4 text-sm"
            data-cursor-hover
          >
            Let's talk <ArrowUpRight size={15} />
          </Link>
          <button
            className="lg:hidden w-10 h-10 rounded-md border hairline grid place-items-center"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-cursor-hover
          >
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      {/* Mobile */}
      {open && (
        <div className="lg:hidden border-t hairline bg-background">
          <div className="px-6 py-6 flex flex-col gap-5">
            {navLinks.map((n) => (
              <NavLink
                key={n.path}
                to={n.path}
                end={n.path === "/"}
                className={({ isActive }) =>
                  `flex items-baseline gap-3 text-2xl font-display ${
                    isActive ? "text-foreground" : "text-muted-foreground"
                  }`
                }
              >
                <span className="font-mono text-xs text-muted-foreground">{n.index}</span>
                {n.label}
              </NavLink>
            ))}
            <Link to="/contact" className="btn-primary w-fit mt-2">
              Let's talk <ArrowUpRight size={15} />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
